/**
 * Tariq AI — contracts shared across the extension, backend, and web app.
 *
 * This file is the single source of truth for everything that crosses a process
 * boundary. It exists in one place for a specific reason: Manifest V3 forbids remote
 * code execution, so DOM handling ships inside the extension bundle while the
 * reasoning stays server-side. That split means the form representation is passed
 * between two independently-deployed artifacts — and a silently-diverged shape there
 * would corrupt exactly the step the product exists to get right.
 *
 * The state unions below mirror Postgres enums in migrations/001_initial_schema.sql.
 * Changing one without the other is a schema/runtime divergence: change both.
 */

// ---------------------------------------------------------------------------
// Domain vocabulary
// ---------------------------------------------------------------------------

/**
 * Sa'y (سعي) — one per-role end-to-end pursuit: research → tailored kit → verification
 * → submit → log. Named by Ahmed, 2026-09-05, and shared with the personal bot because it
 * is the same loop.
 *
 * A Sa'y is NOT the hunt. The hunt is the `run` (see RunTrigger/DepthTier) that surfaces
 * candidates; a Sa'y is what the user starts when they decide to pursue one of them. In
 * the data model a candidate carries its own Sa'y lifecycle through `CandidateState`:
 * `new`/`dismissed` are pre-Sa'y, everything from `researching` onward is a Sa'y in
 * progress. There is deliberately no separate table — split one out only if a candidate
 * ever needs more than one distinct attempt, which nothing yet requires.
 *
 * The word carries the product's promise, which is why it is worth protecting rather than
 * treating as decoration. Sa'y is the earnest repeated seeking — walking between Safa and
 * Marwa — and in ordinary usage it means the effort one is RESPONSIBLE for, with the
 * outcome left to God. That is the locked positioning exactly: we cannot promise
 * interviews, because we do not control employers; we promise that every application goes
 * out as good as if the user had spent two hours on it.
 *
 * So copy that says a Sa'y "gets you hired" uses the name against its own meaning, and is
 * the same outcome-claim overselling that Sonara- and LazyApply-class competitors are
 * distrusted for. Keep user-facing wording on the effort, never on the outcome.
 */
export const SAY = {
  singular: "Sa'y",
  arabic: 'سعي',
  /** "Start a Sa'y" — the primary action a user takes on a surfaced candidate. */
  startAction: "Start a Sa'y",
  /** Pluralised for counts: `3 Sa'y runs this week`. */
  countLabel: (n: number) => `${n} Sa'y run${n === 1 ? '' : 's'}`,
} as const;

// ---------------------------------------------------------------------------
// States (mirror the DB enums exactly)
// ---------------------------------------------------------------------------

export const CANDIDATE_STATES = [
  'new',
  'watch',
  'dismissed',
  'researching',
  'drafting',
  'awaiting_approval',
  'held',
  'awaiting_human_submit',
  'submitted',
] as const;
export type CandidateState = (typeof CANDIDATE_STATES)[number];

export const DEPTH_TIERS = ['deep', 'deeper', 'deepest'] as const;
export type DepthTier = (typeof DEPTH_TIERS)[number];

export const RUN_TRIGGERS = ['scheduled', 'on_demand'] as const;
export type RunTrigger = (typeof RUN_TRIGGERS)[number];

/**
 * Every candidate state needs an explicit presentation mapping. There is no default
 * branch on purpose: adding a state to the union without adding it here is a compile
 * error, not a silent fallback. A `held` candidate rendering as anything
 * success-shaped is the specific bug this prevents.
 */
export const CANDIDATE_STATE_PRESENTATION: Record<
  CandidateState,
  { label: string; tone: 'neutral' | 'progress' | 'attention' | 'success' }
> = {
  new: { label: 'New', tone: 'neutral' },
  // Deliberately 'attention', not 'neutral': a watch is an unresolved judgement the
  // agent could not make confidently, and it exists so that uncertainty stays visible
  // instead of becoming a silent drop. Rendering it as quiet as a dismissal would
  // undo the reason the state exists.
  watch: { label: 'Watching — unsure, worth a look', tone: 'attention' },
  dismissed: { label: 'Dismissed', tone: 'neutral' },
  researching: { label: 'Researching', tone: 'progress' },
  drafting: { label: 'Drafting', tone: 'progress' },
  awaiting_approval: { label: 'Needs your approval', tone: 'attention' },
  held: { label: 'Held — needs review', tone: 'attention' },
  awaiting_human_submit: { label: 'Ready — solve the captcha to submit', tone: 'attention' },
  submitted: { label: 'Submitted', tone: 'success' },
};

// ---------------------------------------------------------------------------
// The form snapshot: extension → backend
// ---------------------------------------------------------------------------

export type FormFieldType =
  | 'text'
  | 'textarea'
  | 'radio'
  | 'checkbox'
  | 'select'
  | 'combobox'
  | 'file'
  | 'hidden'
  | 'unknown';

export interface FormField {
  /** Stable handle the extension can resolve back to a live element. */
  id: string;
  /** Visible label as rendered to a human, not the `name` attribute. */
  label: string;
  type: FormFieldType;
  required: boolean;
  options?: string[];
  /** Pre-filled value found on the page, if any. */
  currentValue?: string | null;
  /**
   * Source-order position. Retained because duplicate-label detection needs to tell
   * "the same question twice" from "two questions that read alike" — the Ashby form
   * that rendered its authorization/sponsorship/background triplet twice produced six
   * answer-groups for three questions, and index alone is what a naive filler trusts.
   */
  domIndex: number;
  /** Fields a human cannot see are bot traps; never fill one. */
  visible: boolean;
  /**
   * True when the extension has confirmed this specific field cannot be answered by ANY
   * in-browser method — not "the agent didn't know what to type," but "no content-script read,
   * synthetic event, or CDP call can reach it at all." First confirmed 2026-09-12 on a real
   * LinkedIn Easy Apply form: a required Citizen/Employment Status control rendered as a native
   * OS-level popup inside a cross-origin iframe, invisible to `document`, unopenable by any
   * dispatched event. Same category as a required file upload (a content script cannot set a
   * file input's value either) — see `decideVerdict`'s `onlyKnownUnfillableField`. Set ONLY by
   * a detector that recognizes a proven-unreachable DOM signature; never a fallback for "the
   * drafter gave up," which stays an ordinary `unanswered_required` finding and a `hold`.
   */
  unreachableInBrowser?: boolean;
  /**
   * True when formSnapshot.ts detected an "Enter manually" (or similar text-entry alternative)
   * toggle/button near this file field — meaning a textarea can be revealed and filled instead
   * of a traditional file upload. Confirmed live on Greenhouse (BRKZ posting): the native
   * "Attach / Dropbox / Google Drive / Enter manually" widget exposes a text-entry path for
   * resume/document fields. fillOrchestrator.ts uses this flag to click the toggle, wait for
   * the textarea, and fill it with the candidate's actual resume content.
   */
  hasManualTextEntry?: boolean;
}

export interface FormSnapshot {
  url: string;
  atsPlatform: string | null;
  capturedAt: string; // ISO 8601
  fields: FormField[];
  /**
   * True when an hCaptcha/reCAPTCHA challenge is present on the page. Detected up front,
   * at snapshot time — before Agent 1 spends a call proposing a payload for a form that
   * cannot be auto-submitted regardless of how clean that payload is. See §6a.
   */
  captchaDetected: boolean;
}

// ---------------------------------------------------------------------------
// Agent 1: propose answers
// ---------------------------------------------------------------------------

export interface ProposedAnswer {
  fieldId: string;
  value: string;
  /** Why this value — surfaced to the user on review, and to Agent 2 as context. */
  rationale: string;
  /**
   * True when the answer restates a stored profile fact (work authorization, salary
   * floor) rather than being composed. Composed prose is a writing task; a restated
   * fact that comes out wrong is a misrepresentation, so the two are reviewed
   * differently and must stay distinguishable.
   */
  fromProfileFact: boolean;
}

export interface ProposedPayload {
  candidateId: string;
  answers: ProposedAnswer[];
  resumeVersionId: string;
  model: string;
}

// ---------------------------------------------------------------------------
// Agent 2: cross-examine
// ---------------------------------------------------------------------------

export const FINDING_KINDS = [
  'duplicate_label',
  'contradictory_required',
  'honeypot',
  'unanswered_required',
  'profile_fact_mismatch',
  /**
   * A salary answer that does not sit at the top of the band THIS posting publishes, while the
   * candidate's `ask_at_band_top` rule is in force. Separate from `profile_fact_mismatch` because
   * the reference it is checked against is the posting, not the stored profile — and the rule
   * deliberately permits an answer below the candidate's own floor, which a profile comparison
   * would flag as wrong.
   */
  'salary_band_mismatch',
  /**
   * A combobox/select answer that is not one of the field's own rendered options — either
   * a near-miss paraphrase (a candidate declined-to-answer phrase from a different EEOC
   * dropdown on the same form) or a fabricated choice that never existed. Confirmed live
   * 2026-09-07 (macair): the drafter free-texted a plausible-sounding value for 4 of 11
   * combobox fields on a real Anthropic Greenhouse posting, none of which matched what the
   * page actually offered. Grounding the prompt in real options (see draftApplication.ts)
   * is the primary fix; this is the deterministic backstop for when that still misses —
   * same reasoning as profile_fact_mismatch: a model can be wrong unpredictably, so the
   * check that actually gates submission must not depend on it being right.
   */
  'invalid_option_value',
] as const;
export type FindingKind = (typeof FINDING_KINDS)[number];

export interface Agent2Finding {
  kind: FindingKind;
  /** Fields implicated. A duplicate_label finding cites all colliding fields. */
  fieldIds: string[];
  detail: string;
}

// ---------------------------------------------------------------------------
// Verdict
// ---------------------------------------------------------------------------

/**
 * `needs_human_submit` (§6a) is a first-class third outcome, not a degraded `hold`: it
 * means every check passed and a captcha — or an unfillable required file input, see
 * `decideVerdict` — is the *only* remaining reason auto-submit can't proceed. A structural
 * `hold` always wins over either of those — see `decideVerdict`.
 */
export type Verdict = 'pass' | 'hold' | 'needs_human_submit';

export interface VerificationReport {
  id: string;
  candidateId: string;
  snapshot: FormSnapshot;
  proposed: ProposedPayload;
  findings: Agent2Finding[];
  agent2Model: string;
  dryRunErrors: string[] | null;
  verdict: Verdict;
  createdAt: string;
}

/**
 * The verdict rule, in one place so it cannot drift between callers.
 *
 * Deliberately not configurable and deliberately not "score above a threshold": any
 * finding at all holds, UNLESS it is exclusively of the one kind an automated agent
 * cannot fill by construction — see below. A hold is cheap — the user reads one extra
 * screen. The failure it prevents is not: an application submitted with the wrong
 * work-authorization answer cannot be recalled, and the user's name is on it, not ours.
 *
 * Once issued, a verdict is final for that report. Re-verification writes a NEW report;
 * nothing downstream may reinterpret this result. The database enforces the same rule
 * with an append-only trigger, because a convention that lives only in code is one
 * refactor away from being lost.
 *
 * `fields` (optional, default none) is what lets this tell "a required field genuinely cannot
 * be answered by any in-browser method" apart from every other `unanswered_required` finding.
 * Two kinds qualify, both structural obstacles rather than drafting mistakes: (a) a required
 * `type: 'file'` field — a content script cannot set a file input's value; (b) a field marked
 * `unreachableInBrowser` — confirmed 2026-09-12 on LinkedIn: a required control rendered as a
 * native OS-level popup inside a cross-origin iframe, unreachable by any content-script read,
 * synthetic event, or CDP call. Same category as a captcha — when EVERY finding is one of
 * these, the outcome is `needs_human_submit`, not `hold`. The finding is never suppressed
 * (confirmed live 2026-09-08: silently dropping the file-upload case once let a Greenhouse
 * resume upload go unfilled with a clean verdict) — only its *consequence* changes, from a
 * dead-end hold to an actionable "a human finishes this one field and submits." Any OTHER
 * finding present at all, even alongside one of these, still holds.
 */
export function decideVerdict(
  findings: Agent2Finding[],
  dryRunErrors: string[] | null,
  captchaDetected: boolean = false,
  fields: FormField[] = [],
): Verdict {
  const unfillableFieldIds = new Set(
    fields.filter((f) => f.type === 'file' || f.unreachableInBrowser === true).map((f) => f.id),
  );
  const onlyKnownUnfillableField = (finding: Agent2Finding) =>
    finding.kind === 'unanswered_required' &&
    finding.fieldIds.length > 0 &&
    finding.fieldIds.every((id) => unfillableFieldIds.has(id));

  if (findings.some((f) => !onlyKnownUnfillableField(f))) return 'hold';
  if (dryRunErrors && dryRunErrors.length > 0) return 'hold';
  if (captchaDetected || findings.length > 0) return 'needs_human_submit';
  return 'pass';
}
